import React, { useState, useEffect } from 'react';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx/xlsx.mjs';
import { Button, Form, Row, Col, Card } from 'react-bootstrap';
import { APP_COLOR } from '../../../const';

const ExcelFormProductManage = (props) => {
    // Get callback function from props
    const { onCustomFormGenerated } = props;
    // Default field definitions with labels and selection state
    // required: true means the field cannot be deselected
    const defaultFields = [
        { id: 'barcode', label: 'Barcode', selected: true, required: true },
        { id: 'sku', label: 'sku', selected: true, required: true },
        { id: 'name', label: 'ຊື່ສິນຄ້າ', selected: true, required: true },
        { id: 'category', label: 'ປະເພດສີນຄ້າ', selected: true, required: true },
        { id: 'unit', label: 'ຫົວຫນວ່ຍ', selected: true, required: true },
        { id: 'size', label: 'ຂະໜາດ', selected: true, required: true },
        { id: 'brand', label: 'ແບຣນ', selected: true, required: true },
        { id: 'subbrand', label: 'ແບຣນຍ່ອຍ', selected: true, required: true },
        { id: 'amount', label: 'ຈໍານວນ', selected: true, required: true },
        { id: 'originPrice', label: 'ລາຄາຕົ້ນທືນ', selected: true, required: true },
        { id: 'promoPrice', label: 'ລາຄາໂປຣໂມຊັ່ນ', selected: true, required: false },
        { id: 'isPromo', label: 'ເປີດໂປຣ', selected: true, required: false },
        { id: 'price', label: 'ລາຄາ', selected: true, required: true },
        { id: 'price1', label: 'ລາຄາ1', selected: true, required: false },
        { id: 'price2', label: 'ລາຄາ2', selected: true, required: false },
        { id: 'price3', label: 'ລາຄາ3', selected: true, required: false },
        { id: 'shippingCost', label: 'ຄ່າຈັດສົ່ງ', selected: true, required: false },
        { id: 'currency', label: 'ສະກຸນເງິນ', selected: true, required: true },
        { id: 'mfg', label: 'ວັນຜະລີດ', selected: true, required: true },
        { id: 'exp', label: 'ວັນຫມົດອາຍຸ', selected: true, required: true },
        { id: 'height', label: 'ຄວາມສູງ', selected: true, required: false },
        { id: 'width', label: 'ຄວາມກ້ວາງ', selected: true, required: false },
        { id: 'length', label: 'ຄວາມຍາວ', selected: true, required: false },
        { id: 'weight', label: 'ນ້ຳໜັກ', selected: true, required: false },
    ];

    // State for field selection
    const [fields, setFields] = useState(defaultFields);
    const [showFieldSelection, setShowFieldSelection] = useState(false);

    // Initialize fields from props if available
    useEffect(() => {
        if (props.dataForm && props.dataForm.length > 0) {
            // Extract header row from dataForm
            const headerRow = props.dataForm[0];
            // Update selected state based on what's in the header
            setFields(fields.map(field => ({
                ...field,
                selected: headerRow.includes(field.label)
            })));
        }
    }, [props.dataForm]);

    // Toggle field selection
    const toggleField = (id) => {
        // Find the field to toggle
        const fieldToToggle = fields.find(field => field.id === id);
        
        // Don't allow toggling if the field is required
        if (fieldToToggle && fieldToToggle.required) {
            return; // Do nothing for required fields
        }
        
        // Toggle the field if it's optional
        setFields(fields.map(field => 
            field.id === id ? { ...field, selected: !field.selected } : field
        ));
    };

    // Select/deselect all fields
    const toggleAllFields = (selectAll) => {
        // Keep required fields selected, only toggle optional fields
        setFields(fields.map(field => ({
            ...field,
            selected: field.required ? true : selectAll
        })));
    };

    // Generate Excel data based on selected fields
    const generateExcelData = () => {
        // Get selected field labels in order
        const selectedLabels = fields
            .filter(field => field.selected)
            .map(field => field.label);

        // If no fields are selected, return empty array
        if (selectedLabels.length === 0) {
            return [];
        }

        // Create header row
        const headerRow = selectedLabels;

        // If we have example data from props, filter it to include only selected fields
        if (props.dataForm && props.dataForm.length > 1) {
            const originalHeader = props.dataForm[0];
            const exampleRows = props.dataForm.slice(1);
            
            // Map each example row to include only selected fields
            const filteredRows = exampleRows.map(row => {
                return selectedLabels.map(label => {
                    const index = originalHeader.indexOf(label);
                    return index !== -1 ? row[index] : '';
                });
            });
            
            return [headerRow, ...filteredRows];
        }

        // Default example data if no props data available
        return [
            headerRow,
            // Example row 1
            selectedLabels.map(label => {
                switch(label) {
                    case 'Barcode': return '00001';
                    case 'sku': return '00001M';
                    case 'ຊື່ສິນຄ້າ': return 'ກະເປົາເງີນ Balenciaga';
                    case 'ປະເພດສີນຄ້າ': return 'ກະເປົາ';
                    case 'ຫົວຫນວ່ຍ': return 'ອັນ';
                    case 'ຂະໜາດ': return '1x2x3';
                    case 'ແບຣນ': return 'Balenciaga';
                    case 'ແບຣນຍ່ອຍ': return 'Balenciaga 2';
                    case 'ຈໍານວນ': return 10;
                    case 'ລາຄາຕົ້ນທືນ': return 50000;
                    case 'ລາຄາໂປຣໂມຊັ່ນ': return 0;
                    case 'ເປີດໂປຣ': return 'FALSE';
                    case 'ລາຄາ': return 900000;
                    case 'ລາຄາ1': return 100000;
                    case 'ລາຄາ2': return 150000;
                    case 'ລາຄາ3': return 250000;
                    case 'ຄ່າຈັດສົ່ງ': return 9000;
                    case 'ສະກຸນເງິນ': return 'LAK';
                    case 'ວັນຜະລີດ': return '2025-04-01';
                    case 'ວັນຫມົດອາຍຸ': return '2025-04-30';
                    case 'ຄວາມສູງ': return 0.5;
                    case 'ຄວາມກ້ວາງ': return 0.5;
                    case 'ຄວາມຍາວ': return 0.5;
                    case 'ນ້ຳໜັກ': return 0.5;
                    default: return '';
                }
            }),
            // Example row 2
            selectedLabels.map(label => {
                switch(label) {
                    case 'Barcode': return '00003';
                    case 'sku': return '00003M';
                    case 'ຊື່ສິນຄ້າ': return 'ກະເປົາເງີນ Dior';
                    case 'ປະເພດສີນຄ້າ': return 'ກະເປົາ';
                    case 'ຫົວຫນວ່ຍ': return 'ອັນ';
                    case 'ຂະໜາດ': return '1x2x3';
                    case 'ແບຣນ': return 'Dior';
                    case 'ແບຣນຍ່ອຍ': return 'Dior 2';
                    case 'ຈໍານວນ': return 10;
                    case 'ລາຄາຕົ້ນທືນ': return 5;
                    case 'ລາຄາໂປຣໂມຊັ່ນ': return 0;
                    case 'ເປີດໂປຣ': return 'FALSE';
                    case 'ລາຄາ': return 10;
                    case 'ລາຄາ1': return 7;
                    case 'ລາຄາ2': return 10;
                    case 'ລາຄາ3': return 15;
                    case 'ຄ່າຈັດສົ່ງ': return 0.5;
                    case 'ສະກຸນເງິນ': return 'USD';
                    case 'ວັນຜະລີດ': return '2025-04-01';
                    case 'ວັນຫມົດອາຍຸ': return '2025-12-30';
                    case 'ຄວາມສູງ': return 0.5;
                    case 'ຄວາມກ້ວາງ': return 0.5;
                    case 'ຄວາມຍາວ': return 0.5;
                    case 'ນ້ຳໜັກ': return 0.5;
                    default: return '';
                }
            }),
            // Example row 2
            selectedLabels.map(label => {
                switch(label) {
                    case 'Barcode': return '00002';
                    case 'sku': return '00002M';
                    case 'ຊື່ສິນຄ້າ': return 'ກະເປົາເງີນ Gucci';
                    case 'ປະເພດສີນຄ້າ': return 'ກະເປົາ';
                    case 'ຫົວຫນວ່ຍ': return 'ອັນ';
                    case 'ຂະໜາດ': return '1x2x3';
                    case 'ແບຣນ': return 'Gucci';
                    case 'ແບຣນຍ່ອຍ': return 'Gucci 2';
                    case 'ຈໍານວນ': return 10;
                    case 'ລາຄາຕົ້ນທືນ': return 100;
                    case 'ລາຄາໂປຣໂມຊั່ນ': return 0;
                    case 'ເປີດໂປຣ': return 'FALSE';
                    case 'ລາຄາ': return 200;
                    case 'ລາຄາ1': return 199;
                    case 'ລາຄາ2': return 250;
                    case 'ລາຄາ3': return 300;
                    case 'ຄ່າຈັດສົ່ງ': return 9;
                    case 'ສະກຸນເງິນ': return 'BAHT';
                    case 'ວັນຜະລີດ': return '2025-04-01';
                    case 'ວັນຫມົດອາຍຸ': return '2025-12-30';
                    case 'ຄວາມສູງ': return 0.5;
                    case 'ຄວາມກ້ວາງ': return 0.5;
                    case 'ຄວາມຍາວ': return 0.5;
                    case 'ນ້ຳໜັກ': return 0.5;
                    default: return '';
                }
            }),
        ];
    };

    // Handle download
    const handleDownload = () => {
        const excelData = generateExcelData();
        
        if (excelData.length === 0) {
            alert('ກະລຸນາເລືອກຢ່າງໜ້ອຍໜຶ່ງຟິວ');
            return;
        }

        // Create a new workbook and worksheet
        const workbook = XLSX.utils.book_new();
        const worksheet = XLSX.utils.aoa_to_sheet(excelData);

        // Add the worksheet to the workbook
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');

        // Convert the workbook to an array buffer
        const arrayBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });

        // Save the Excel file using FileSaver.js
        const file = new Blob([arrayBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        saveAs(file, 'product_template.xlsx');

        // If callback is provided, pass the generated Excel data to parent component
        // Also pass true to indicate the Excel file has been downloaded
        if (onCustomFormGenerated && typeof onCustomFormGenerated === 'function') {
            onCustomFormGenerated(excelData, true);
        }

        // Hide field selection after download
        setShowFieldSelection(false);
    };
    
    // Handle Next Step
    const handleNextStep = () => {
        const excelData = generateExcelData();
        
        if (excelData.length === 0) {
            alert('ກະລຸນາເລືອກຢ່າງໜ້ອຍໜຶ່ງຟິວ');
            return;
        }

        // If callback is provided, pass the generated Excel data to parent component
        // Also pass true to indicate the Excel file has been downloaded
        if (onCustomFormGenerated && typeof onCustomFormGenerated === 'function') {
            onCustomFormGenerated(excelData, true);
        }

        // Hide field selection after download
        setShowFieldSelection(false);
    };

    return (
        <div>
            {!showFieldSelection ? (
                <Button 
                    onClick={() => setShowFieldSelection(true)} 
                    style={{ 
                        borderColor: APP_COLOR, 
                        width: 200, 
                        color: APP_COLOR, 
                        backgroundColor: 'transparent', 
                        fontWeight: 'bold', 
                        whiteSpace: 'nowrap' 
                    }}
                >
                    ດາວໂຫຼດຕົວຢ່າງ Excel
                </Button>
            ) : (
                <Card className="mb-3 p-3">
                    <h5>ເລືອກຟິວທີ່ຕ້ອງການສົ່ງອອກ</h5>
                    <div className="mb-2">
                        <small style={{ color: 'red' }}>* ຟິວທີ່ຈໍາເປັນຕ້ອງມີ</small>
                    </div>
                    <div className="mb-2">
                        <Button 
                            variant="outline-primary" 
                            size="sm" 
                            className="me-2" 
                            onClick={() => toggleAllFields(true)}
                        >
                            ເລືອກທັງໝົດ
                        </Button>
                        <Button 
                            variant="outline-secondary" 
                            size="sm" 
                            onClick={() => toggleAllFields(false)}
                        >
                            ຍົກເລີກທັງໝົດ
                        </Button>
                    </div>
                    
                    <Row className="mb-3">
                        {fields.map((field) => (
                            <Col key={field.id} xs={6} md={4} lg={3}>
                                <Form.Check
                                    type="checkbox"
                                    id={`field-${field.id}`}
                                    label={
                                        <span>
                                            {field.label}
                                            {field.required && (
                                                <span style={{ color: 'red', marginLeft: '5px' }}>
                                                    *
                                                </span>
                                            )}
                                        </span>
                                    }
                                    checked={field.selected}
                                    onChange={() => toggleField(field.id)}
                                    disabled={field.required}
                                />
                            </Col>
                        ))}
                    </Row>
                    
                    <div className="d-flex">
                        <Button 
                            onClick={handleDownload} 
                            style={{ 
                                borderColor: APP_COLOR, 
                                color: APP_COLOR, 
                                backgroundColor: 'transparent', 
                                fontWeight: 'bold',
                                marginRight: '10px'
                            }}
                        >
                            ດາວໂຫຼດ Excel
                        </Button>
                        <Button 
                            onClick={handleNextStep} 
                            style={{ 
                                borderColor: APP_COLOR, 
                                color: APP_COLOR, 
                                backgroundColor: 'transparent', 
                                fontWeight: 'bold',
                                marginRight: '10px'
                            }}
                        >
                            ຕໍ່ໄປ
                        </Button>
                        <Button 
                            variant="outline-secondary" 
                            onClick={() => setShowFieldSelection(false)}
                        >
                            ຍົກເລີກ
                        </Button>
                    </div>
                </Card>
            )}
        </div>
    );
};

export default ExcelFormProductManage;
